/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import i18n from '@/i18n';
import AccessTokensApiService from '@/services/api/access-tokens.api-service';
import MsConfigurationApiService from '@/services/api/ms-configuration.api-service';
import AccessTokenModel from '@/model/access-token/accessToken.model';

const MAX_EXPIRATION_DAYS_FALLBACK = 365;

const neverUsedText = i18n.global.t('users.accessTokens.neverUsed');

const useAccessTokensStore = defineStore('accessTokens', {
  state: () => ({
    accessTokens: [],
    selectedAccessToken: null,
    accessTokenToCopy: null,
    copyCounter: 0,
    maxExpirationDays: MAX_EXPIRATION_DAYS_FALLBACK,
  }),

  actions: {
    setSelectedAccessToken(token) {
      this.selectedAccessToken = token;
    },
    clearSelectedAccessToken() {
      this.selectedAccessToken = null;
    },
    setSelectedTokenFromList(id) {
      this.selectedAccessToken = this.accessTokens.find((token) => token.id === id) || null;
    },
    setAccessTokenToCopy(token) {
      this.accessTokenToCopy = token;
      // bump counter so components can react/remount when copying the same token repeatedly
      this.copyCounter = (this.copyCounter || 0) + 1;
    },
    clearAccessTokenToCopy() {
      this.accessTokenToCopy = null;
    },

    async fetchAccessTokens() {
      const accessTokens = await AccessTokensApiService.getAccessTokens();
      this.accessTokens = accessTokens.map((token) => new AccessTokenModel(token, neverUsedText));
      return this.accessTokens;
    },

    async getMaxExpirationDaysValue() {
      try {
        const response = await MsConfigurationApiService.getConfigurationSection(['accessTokens']);
        const config = response?.accessTokens;
        const days = Number(config?.maxExpirationDays);
        this.maxExpirationDays = Number.isFinite(days) && days > 0 ? days : MAX_EXPIRATION_DAYS_FALLBACK;
        return this.maxExpirationDays;
      } catch {
        this.maxExpirationDays = MAX_EXPIRATION_DAYS_FALLBACK;
        return this.maxExpirationDays;
      }
    },

    async generateAccessToken(name, expirationDate, permissions) {
      const response = await AccessTokensApiService.generateAccessToken(name, expirationDate, permissions);
      const createdToken = new AccessTokenModel(response, neverUsedText);
      this.accessTokens.push(createdToken);
      this.clearAccessTokenToCopy();
      return { ...createdToken, token: response.token };
    },
    async revokeAccessToken(tokenId) {
      const response = await AccessTokensApiService.revokeAccessToken(tokenId);

      const tokenIndex = this.accessTokens.findIndex((token) => token.id === tokenId);
      if (tokenIndex !== -1 && response) {
        this.accessTokens[tokenIndex] = new AccessTokenModel(response, neverUsedText);
      }
    },

    async deleteAccessToken(tokenId) {
      await AccessTokensApiService.deleteAccessToken(tokenId);
      this.accessTokens = this.accessTokens.filter((token) => token.id !== tokenId);
    },

    /**
     * @description Handle the event when an access token is updated (revoked or expired)
     * @param {object} token - The updated access token object
     * @returns {void}
     * @example
     * handleAccessTokenUpdatedEvent({
     *   _id: 'tokenId',
     *   status: 'revoked',
     *   revokedAt: '2024-06-01T12:00:00Z'
     * });
     */
    handleAccessTokenUpdatedEvent(token) {
      if (!token) {
        return;
      }
      const updatedTokenId = token._id || token.id;
      const newStatus = token.status;
      if (!updatedTokenId || !newStatus) {
        return;
      }
      const existingTokenIndex = this.accessTokens.findIndex((t) => t.id === updatedTokenId);
      if (existingTokenIndex === -1) {
        return;
      }
      // token can not be updated, only revoked or expired,
      // updating status always and revocation info if available
      this.accessTokens[existingTokenIndex].status = token.status;
      if (token.lastUsedAt) {
        this.accessTokens[existingTokenIndex].lastUsed = AccessTokenModel.formatDate(token.lastUsedAt, neverUsedText);
      }
      if (newStatus === 'revoked') {
        this.accessTokens[existingTokenIndex].revocationInfo = token.revocationInfo;
        this.accessTokens[existingTokenIndex].revokedAt = token.revokedAt;
      }
    },
  },
});

export default useAccessTokensStore;
